import { FC, Suspense, useCallback, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/reduxhooks";
import * as style from "./Layout.module.scss";
import TopMenu from "./TopMenu/TopMenu";
import Dialog from "../../components/Dialog/Dialog";
import { LEAVE_DIALOG_TEXT, LEAVE_DIALOG_TITLE } from "../../utils/messages";
import {
  gipFormPages,
  glFormPages,
  rehabFormPages,
  cicFormPages,
  IPage,
} from "../Breadcrumbs/formPages";
import { OauthLogout } from "utils/User/logout";
import { getPage } from "pages/Pages";
import useNavbar from "hooks/CMS/Navbar/useNavbar";
import Navbar from "./nestedNavbar/NestedNavbar";
import { isInternalNavLink, isNavLink } from "types/CMS/Unum/Navbar/navLink";
import { navItem } from "types/CMS/Unum/Navbar/navbar";
import { isNavDraw } from "types/CMS/Unum/Navbar/navDraw";

type layoutProps = {
  showBackground?: boolean;
};

const Layout: FC<layoutProps> = ({ showBackground = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const policyDetls: any = useAppSelector(
    (state: any) => state.ClaimSlice.policyDetls
  );
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [navigatingTo, setNavigatingTo] = useState("");
  const { navItems, isLoading } = useNavbar();

  const isUrlAFormJounery = (url: string) => {
    const jounerys = [
      ...gipFormPages,
      ...glFormPages,
      ...rehabFormPages,
      ...cicFormPages,
    ];
    return (
      jounerys.find((formURl: IPage) => {
        return formURl.url
          .toLowerCase()
          .split("/")
          .pop()
          ?.includes(url.toLowerCase().split("/").pop() ?? "");
      }) !== undefined
    );
  };

  const isDialogRequired = useCallback(
    (
      e: React.MouseEvent<HTMLElement, MouseEvent>,
      navigatingTo: string
    ): boolean => {
      if (
        isUrlAFormJounery(location.pathname) &&
        navigatingTo !== process.env.REACT_APP_CASHPLAN_REDIRECT_URL
      ) {
        e.preventDefault();
        setShowLeaveDialog(true);
        setNavigatingTo(navigatingTo);
        return true;
      }
      return false;
    },
    [location]
  );

  let title = "Employer Portal";
  let showHelp = false;

  const currentPage = getPage(location.pathname, policyDetls.product);
  if (currentPage) {
    title = currentPage.pageTitle;
    showHelp = currentPage.showHelpButton;
  }

  const onNavItemClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    navItem: navItem
  ) => {
    // do nothing for navdraw
    if (isNavDraw(navItem)) {
      return;
    }
    if (isNavLink(navItem)) {
      if (isInternalNavLink(navItem)) {
        !isDialogRequired(e, navItem.link) && navigate(navItem.link);
        return;
      }
      window.open(navItem.link, "_blank");
    }
  };

  return (
    <>
      <div className={style.LayoutHeader}>
        <TopMenu title={title} showHelp={showHelp}></TopMenu>
      </div>
      <div>
        <Navbar
          isLoading={isLoading}
          activeLink={location.pathname}
          navBar={{ items: navItems, type: "navbar" }}
          onClick={onNavItemClick}
          onLogout={() => {
            OauthLogout();
          }}
        />
      </div>
      {showBackground && (
        <div
          className={style.LayoutBackground}
          data-testid="LayoutBackground"
        ></div>
      )}
      <main className={style.LayoutContent} style={{ paddingTop: "60px" }}>
        <div>
          <Suspense fallback={<div style={{ margin: "10px" }}>Loading...</div>}>
            <Outlet />
          </Suspense>
        </div>
      </main>
      <Dialog
        open={showLeaveDialog}
        onClose={() => {
          setShowLeaveDialog(false);
        }}
        title={LEAVE_DIALOG_TITLE}
        message={LEAVE_DIALOG_TEXT}
        secndryBtnText="Cancel"
        primaryBtnText="Exit"
        primaryBtnColor="success"
        primaryBtnAction={() => {
          setShowLeaveDialog(false);
          navigate(navigatingTo);
        }}
      />
    </>
  );
};

export default Layout;
